import { dynamicString } from '@home/utils/dynamicString';

interface IGameInfo {
  game_name: string;
  entity_name?: string;
  entity_name_cn?: string;
  publisher?: string;
  cover?: string;
  country_abbre?: string;
  country_name_en?: string;
  [key: string]: string | undefined;
}

export const flattenMetricsList = (
  metricsList: Array<Record<string, unknown>>,
  games?: IGameInfo[],
  studioIconMap?: Record<string, string>,
  pageOffset = 0,
  isEN = true,
): Array<Record<string, unknown>> => {
  const gameMap = new Map<string, IGameInfo>();
  if (games?.length) {
    games.forEach(g => {
      [g.game_name, g.entity_name, g.entity_name_cn]
        .filter(Boolean)
        .forEach(key => {
          gameMap.set(key as string, g);
        });
    });
  }

  return metricsList.map((item, index) => {
    const customDims = item.custom_dimensions as Array<Record<string, string>> | undefined;
    const gameDim = customDims?.find(d => d.key === 'game_code');
    const studioDim = customDims?.find(d => d.key === 'studio');
    const categoryDim = customDims?.find(d => d.key === 'category');
    const gameName = gameDim?.value ?? '';
    const studioName = studioDim?.value ?? '';
    const categoryName = categoryDim?.values ?? categoryDim?.value ?? '';
    const gameInfo = gameMap.get(gameName);
    const metrics = item.metrics as Record<string, Record<string, string>> | undefined;
    const flatMetrics: Record<string, unknown> = {};
    if (metrics) {
      Object.keys(metrics).forEach(key => {
        flatMetrics[key] = metrics[key]?.value ?? '';
        flatMetrics[`${key}_dod`] = metrics[key]?.dod ?? '';
        flatMetrics[`${key}_dod_value`] = metrics[key]?.dod_value ?? '';
        flatMetrics[`${key}_dod_date`] = metrics[key]?.dod_date ?? '';
      });
    }
    const localeGameName = isEN
      ? (gameInfo?.entity_name ?? gameInfo?.entity_name_cn)
      : (gameInfo?.entity_name_cn ?? gameInfo?.entity_name);

    return Object.assign(
      {},
      item,
      flatMetrics,
      {
        rank: pageOffset + index + 1,
        game_chart_name: gameName,
        game_name: localeGameName ?? gameName,
        game_icon: gameInfo?.cover ?? '',
        country_code: gameInfo?.country_abbre ?? '',
        country_name: gameInfo?.publisher ?? '',
        studio_name: studioName,
        studio_icon: studioIconMap?.[studioName] ?? '',
        category: categoryName,
      },
    );
  });
};

interface ISparklineGroupItem {
  date: string;
  metrics: Record<string, Record<string, string>>;
}

const DIMENSION_FIELD_MAP: Record<string, string> = {
  game_code: 'game_chart_name',
  studio: 'studio_name',
  category: 'category',
};

const buildKeyFromDims = (
  dims: Array<Record<string, string>> | undefined,
): string => {
  if (!dims?.length) return '';
  return dims
    .map(d => `${d.key}\0${d.values ?? d.value ?? ''}`)
    .sort()
    .join('\x01');
};

const buildKeyFromRow = (
  row: Record<string, unknown>,
  dimKeys: string[],
): string => {
  return dimKeys
    .map(k => `${k}\0${String(row[DIMENSION_FIELD_MAP[k] ?? k] ?? '')}`)
    .sort()
    .join('\x01');
};

export const mergeSparklineData = (
  flatList: Array<Record<string, unknown>>,
  sparklineMetrics: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> => {
  const sparklineMap = new Map<string, ISparklineGroupItem[]>();
  const dimKeysSet = new Set<string>();

  sparklineMetrics.forEach(item => {
    const customDims = item.custom_dimensions as Array<Record<string, string>> | undefined;
    if (!customDims?.length) return;

    customDims.forEach(d => dimKeysSet.add(d.key));
    const compositeKey = buildKeyFromDims(customDims);
    if (!compositeKey) return;

    if (!sparklineMap.has(compositeKey)) {
      sparklineMap.set(compositeKey, []);
    }
    sparklineMap.get(compositeKey)!.push({
      date: String(item.date ?? ''),
      metrics: (item.metrics as Record<string, Record<string, string>>) ?? {},
    });
  });

  sparklineMap.forEach(items => {
    items.sort((a, b) => a.date.localeCompare(b.date));
  });

  const dimKeys = Array.from(dimKeysSet);

  return flatList.map(row => {
    const compositeKey = buildKeyFromRow(row, dimKeys);
    const items = sparklineMap.get(compositeKey);
    if (!items?.length) return row;

    const dates = items.map(i => {
      const d = i.date;
      if (/^\d{8}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
      if (/^\d{6}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 6)}`;
      return d;
    });
    const metricKeys = new Set<string>();
    items.forEach(i => {
      Object.keys(i.metrics).forEach(k => metricKeys.add(k));
    });

    const sparklineFields: Record<string, unknown> = {
      sparkline_dates: dates,
    };

    // 仅拼装 sparkline 趋势数组，不用趋势图接口的数据回填单元格的 value / dod
    // （累计/月度的值与环比一律以主接口为准，主接口没数据就保持为空）
    metricKeys.forEach(key => {
      sparklineFields[`sparkline_${key}`] = items.map(i => {
        const val = i.metrics[key]?.value;
        return val !== undefined && val !== '' ? parseFloat(val) : '';
      });
    });

    return Object.assign({}, row, sparklineFields);
  });
};

export interface IDateSubtitleConfig {
  templateEn?: string;
  templateCn?: string;
  dateSeparator?: string;
  /** 从 propsData[subtitleEndTimeDataKey] 读取实际数据截止时间（YYYY-MM-DD 字符串），替换 filter.end_time */
  subtitleEndTimeDataKey?: string;
}

export const getDateRangeSubtitle = (
  startTime?: string,
  endTime?: string,
  config?: IDateSubtitleConfig,
): { en: string; cn: string; } => {
  const sep = config?.dateSeparator ?? '-';
  const formatYM = (t: string) => t.slice(0, 7).replace('-', sep);
  const start = startTime ? formatYM(startTime) : '';
  const end = endTime ? formatYM(endTime) : '';
  if (!start || !end) return { en: '', cn: '' };
  const vars = { start, end };
  const { templateEn = '', templateCn = '' } = config || {};
  return {
    en: templateEn ? dynamicString(templateEn, vars) : '',
    cn: templateCn ? dynamicString(templateCn, vars) : '',
  };
};
